import DateFnsUtils from '@date-io/date-fns';
import { Grid, GridProps } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ICreateFileResponse } from 'api/file/interfaces/create-file';
import { format as formatDate } from 'date-fns';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IDomesticAddressFields } from 'ui-kit/form/interfaces/AddressModal';
import StateSelectField from 'ui-kit/inputs/StateSelectField';
import { IAgentNetBaseFormGroupProps } from 'utilities/form/components/base-form';
import { getFilteredOfficesByPreference } from 'utilities/preferred-office-utils';
import { TransactionTypeValue } from '../../../../api/file/constants';
import {
  getAccountIdByOfficeAndUnderwriterAndState,
  getDefaultOffice,
  getOfficeOptionsByState,
  getStateOptions,
  getStateOptionsByOffice,
  getUnderwriterOptionsByOfficeAndState,
} from '../../../../api/profile/profile-utilities';
import AgentNetDropdownSelector, { SelectOption } from '../../../../ui-kit/inputs/AgentNetDropdownSelector';
import CurrencyField from '../../../../ui-kit/inputs/CurrencyField/CurrencyField';
import DateFieldString from '../../../../ui-kit/inputs/DateField/DateFieldString';
import { AgentNetTextInput } from '../../../../ui-kit/inputs/TextField';
import { DEFAULT_OFFICE_LABEL, transactionTypes } from '../../../constants';
import { IGetLenderResponse } from '../../parties/interfaces/CreateLenderRequest';
import './FileInformationForm.scss';

interface FileInfoCreateFormProps extends IAgentNetBaseFormGroupProps<ICreateFileResponse> {
  ContainerComponentProps?: GridProps;
  isAdmin?: boolean;
}

const FileInfoCreateForm: React.FC<FileInfoCreateFormProps> = ({
  ContainerComponentProps,
  showAllValidation,
  value,
  validationErrors,
  onChange,
}: FileInfoCreateFormProps) => {
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm, preferredOffice } = profileCtx;
  /* Initialize with the preferred-office filtered firm so the default-office logic uses the right set */
  const [userFirmPreferences] = useState(() => {
    const { filteredOffices } = getFilteredOfficesByPreference(preferredOffice, userFirm?.offices, userFirm?.firmId);
    if (preferredOffice?.length > 0 && userFirm) {
      return { ...userFirm, offices: filteredOffices };
    }
    return userFirm;
  });
  /* Tracks whether the default office has already been applied so it only happens once per form */
  const defaultOfficeAppliedRef = useRef(false);
  /* Skips the state-driven cascade that clears the office when the state is set programmatically */
  const skipStateCascadeRef = useRef(false);

  useEffect(() => {
    if (value?.officeId) {
      const options = getUnderwriterOptionsByOfficeAndState(
        userFirmPreferences,
        Number(value.officeId),
        value.stateCode,
      );
      if (options?.length === 1) {
        handleUnderwriterCode(options[0].value);
      } else {
        handleUnderwriterCode('');
      }
    }
  }, [value?.officeId]);

  useEffect(() => {
    /* Preserve the programmatically pre-selected office instead of clearing it on the state cascade */
    if (skipStateCascadeRef.current) {
      skipStateCascadeRef.current = false;
      return;
    }
    if (value?.stateCode) {
      const officeOptions = getOfficeOptionsByState(userFirmPreferences, value.stateCode);
      if (officeOptions?.length === 1) {
        handleOfficeChange(officeOptions[0].value);
      } else {
        handleOfficeChange('');
      }
    }
  }, [value?.stateCode]);

  /* Pre-select the firm's default office (and its state) when the form opens empty */
  useEffect(() => {
    if (defaultOfficeAppliedRef.current || !userFirmPreferences?.offices || value?.stateCode || value?.officeId) {
      return;
    }
    const defaultOffice = getDefaultOffice(userFirmPreferences);
    if (!defaultOffice) return;
    const stateOptions = getStateOptionsByOffice(userFirmPreferences, defaultOffice.officeId);
    if (!stateOptions.length) return;
    defaultOfficeAppliedRef.current = true;
    skipStateCascadeRef.current = true;
    const stateCode = stateOptions[0].value;
    const properties = value.properties?.map((property, index) =>
      index === 0 ? { ...property, state: stateCode } : property,
    );
    onChange({
      ...value,
      stateCode,
      properties,
      officeId: defaultOffice.officeId,
    } as IDomesticAddressFields);
  }, [userFirmPreferences?.offices]);

  useEffect(() => {
    if (!defaultOfficeAppliedRef.current && userFirmPreferences?.offices && !value?.stateCode) {
      const stateOptions = getStateOptions(userFirmPreferences);
      if (stateOptions?.length === 1) {
        handleStateChange(stateOptions[0].value);
      }
    }
  }, [userFirmPreferences?.offices]);

  /* Append the "(Default Office)" suffix to the firm's default office in the dropdown */
  const officeOptions = useMemo(() => {
    const options = getOfficeOptionsByState(userFirmPreferences, value.stateCode);
    const defaultOffice = getDefaultOffice(userFirmPreferences);
    if (!defaultOffice) return options;
    return options.map((option) =>
      option.value === defaultOffice.officeId.toString()
        ? { ...option, name: `${option.name} ${DEFAULT_OFFICE_LABEL}` }
        : option,
    );
  }, [userFirmPreferences, value.stateCode]);

  const handleUnderwriterCode = (val: string) => {
    const underwriterCode = val;
    /* Update the underwriter and the accountId */
    const accountId = getAccountIdByOfficeAndUnderwriterAndState(
      userFirmPreferences,
      Number(value.officeId),
      value.stateCode,
      underwriterCode,
    );
    onChange({ ...value, underwriterCode: underwriterCode, accountId: accountId });
  };

  const handleStateChange = (stateSelection: string) => {
    if (stateSelection) {
      value.stateCode = stateSelection;
    }
    const properties = value.properties?.map((property, index) => {
      if (index === 0) {
        return { ...property, state: stateSelection };
      }
      return property;
    });

    /* In addition to setting the state code, clear the office and underwriter selections */
    onChange({
      ...value,
      stateCode: stateSelection,
      properties: properties,
      officeId: '',
      underwriterCode: '',
    } as IDomesticAddressFields);
  };

  const handleOfficeChange = (val: string) => {
    const officeId = +val;
    /* If there is just one underwriter for this office, use it to set the accountId also */
    const underWriters: SelectOption[] = getUnderwriterOptionsByOfficeAndState(
      userFirmPreferences,
      officeId,
      value.stateCode,
    );
    if (underWriters && underWriters.length === 1) {
      const accountId = getAccountIdByOfficeAndUnderwriterAndState(
        userFirmPreferences,
        officeId,
        value.stateCode,
        underWriters[0].value,
      );
      /* Update the officeId and accountId */
      onChange({ ...value, officeId: +val, accountId: accountId });
    } else {
      /* Update the officeId only */
      onChange({ ...value, officeId: +val });
    }
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      {value && onChange && (
        <Grid container {...ContainerComponentProps} spacing={3}>
          <Grid item sm={3}>
            <AgentNetTextInput
              fullWidth
              required
              variant="outlined"
              label="File Number"
              id="fileNumber"
              name={'fileNumber'}
              value={value.fileNumber}
              onChange={(e) => onChange({ ...value, fileNumber: e.target.value })}
              errs={validationErrors}
              showValidation={showAllValidation}
              data-testid="CreateFileFileNumber"
            />
          </Grid>

          <Grid item sm={3}>
            {!userFirmPreferences?.offices ? (
              <StateSelectField
                label="State"
                name="stateCode"
                id="stateCode"
                required
                showValidation={showAllValidation}
                errs={validationErrors}
                value={value.stateCode}
                onChange={(e) => {
                  const properties = value.properties?.map((property, index) => {
                    if (index === 0) {
                      return { ...property, state: e };
                    }
                    return property;
                  });
                  onChange({ ...value, stateCode: e, properties: properties } as IDomesticAddressFields);
                }}
                data-testid="CreateFileState"
              />
            ) : (
              <>
                <StateSelectField
                  label="State"
                  name="stateCode"
                  id="stateCode"
                  required
                  stateOptions={getStateOptions(userFirmPreferences).map((e: any) => {
                    return { name: e.name, abbreviation: e.value };
                  })}
                  value={value.stateCode}
                  showValidation={showAllValidation}
                  errs={validationErrors}
                  onChange={(e) => {
                    const properties = value.properties?.map((property, index) => {
                      if (index === 0) {
                        return { ...property, state: e };
                      }
                      return property;
                    });
                    onChange({
                      ...value,
                      stateCode: e,
                      properties: properties,
                      officeId: value.officeId ? value.officeId : '',
                      underwriterCode: value.underwriterCode ? value.underwriterCode : '',
                    } as IDomesticAddressFields);
                  }}
                  data-testid="CreateFileState"
                />
              </>
            )}
          </Grid>
          <Grid item sm={6}>
            <AgentNetDropdownSelector
              name="officeId"
              label="Office"
              disabled={!value?.stateCode}
              required
              options={officeOptions}
              value={value?.officeId}
              id="officeId"
              menuOption={handleOfficeChange}
              dropdowntype="outlined"
              errs={validationErrors}
              showValidation={showAllValidation}
              data-testid="CreateFileOffice"
            />
          </Grid>
          <Grid item sm={6}>
            <AgentNetDropdownSelector
              name="underwriterCode"
              label="Underwriter"
              disabled={!value?.officeId}
              required
              options={getUnderwriterOptionsByOfficeAndState(
                userFirmPreferences,
                Number(value.officeId),
                value.stateCode,
              )}
              value={value?.underwriterCode}
              id="accountId"
              menuOption={handleUnderwriterCode}
              dropdowntype="outlined"
              errs={validationErrors}
              showValidation={showAllValidation}
              data-testid="CreateFileUnderwriter"
            />
          </Grid>
          <Grid item sm={6}>
            <AgentNetDropdownSelector
              name="transactionType"
              label="Transaction Type"
              options={transactionTypes}
              value={value.transactionType}
              id="transactionType"
              menuOption={(val: string) => {
                onChange({ ...value, transactionType: val as TransactionTypeValue });
              }}
              dropdowntype="outlined"
              errs={validationErrors}
              showValidation={showAllValidation}
              data-testid="CreateFileTransactionType"
            />
          </Grid>

          <Grid item sm={3}>
            <CurrencyField
              variant="outlined"
              fullWidth
              label={'Sales Price'}
              id="salesprice"
              name={'salesPrice'}
              errs={validationErrors}
              value={value?.salesPrice}
              max={100000000000}
              allowNegative={false}
              onChange={(e) => onChange({ ...value, salesPrice: +e.target.value })}
              data-testid="CreateFileSalesPrice"
            />
          </Grid>
          <Grid item sm={3}>
            <CurrencyField
              variant="outlined"
              fullWidth
              label={'Loan Amount'}
              id="totalLoanAmount"
              defaultValue={0}
              name={'loanAmount'}
              errs={validationErrors}
              value={value?.loanAmount}
              max={100000000000}
              allowNegative={false}
              onChange={(e) => {
                const selectedAmount = e.target.value;
                // Also update the lender's field
                const lender: IGetLenderResponse = value.lenders ? value.lenders[0] : {};
                const updatedLender = { ...lender, loanAmount: selectedAmount };
                onChange({ ...value, loanAmount: selectedAmount, lenders: [updatedLender] });
              }}
              data-testid="CreateFileLoanAmount"
            />
          </Grid>
          <Grid item sm={3}>
            <DateFieldString
              label="Closing Date"
              id="calender"
              maxDate="12/31/9999"
              value={(() => {
                const dateParsed = Date.parse(value?.closingDate || '');
                return dateParsed ? formatDate(dateParsed, 'MM/dd/yyyy') : null;
              })()}
              onChange={(dateVal: string | null) => {
                value && onChange({ ...value, closingDate: dateVal ? dateVal : null });
              }}
              qaAttribute="CreateFileClosingDate"
              error={showAllValidation && validationErrors?.some((valError) => valError.field === 'closingDate')}
            />
          </Grid>
        </Grid>
      )}
    </MuiPickersUtilsProvider>
  );
};

export default FileInfoCreateForm;
